import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  Card,
  CardHeader,
  Field,
  Label,
  makeStyles,
  Option,
} from '@fluentui/react-components';
import { HelpButton } from '@bingads-webui-react/help-popup';
import { countries } from '../../countries';
import { errorMessages } from '../../errors';
import { cardHeaderStyles } from '../../page-layout';
import { sortByName } from '../../utils';
import { RequiredDropdown } from '../validated/required-dropdown';

const useStyles = makeStyles(cardHeaderStyles);

export const CountryCard = ({ country, onChangeCountry, showError }) => {
  const styles = useStyles();
  const i18n = useContext(I18nContext);
  const countryLabel = i18n.getString(_TL_('Country'));
  const learnMore = i18n.getString(_TL_('Click to learn more about'));
  const countryKeyValues = useMemo(() => Object.values(countries).sort(sortByName(i18n)), [i18n]);
  const countryAsArray = useMemo(() => (country ? [country] : []), [country]);

  const onOptionSelect = useCallback((e, data) => {
    onChangeCountry(data.selectedOptions[0]);
  }, [onChangeCountry]);

  const [fieldProps, setFieldProps] = useState(null);

  const onValidateCountry = useCallback(({ isValid, messages }) => {
    if (isValid) {
      setFieldProps(null);
    } else {
      setFieldProps({
        validationMessage: i18n.getString(errorMessages[messages[0]]),
        validationState: 'error',
      });
    }
  }, [i18n]);

  return (
    <Card className="country-card">
      <CardHeader
        header={
          <div className={styles.cardHeader}>
            <Label size="large" className={styles.label} required>{countryLabel}</Label>
            <HelpButton
              aria-label={`${learnMore} ${countryLabel}`}
              topic="pop_BA_Experiments_LiftCountry"
            />
          </div>
        }
      />
      <Field {...(showError ? fieldProps : null)}>
        <RequiredDropdown
          ariaLabel={i18n.getString(_TL_('Lift measurement country dropdown'))}
          errorMessage="COUNTRY_REQUIRED"
          onOptionSelect={onOptionSelect}
          value={country ? i18n.getString(countries[country].name) : ''}
          validationProps={{
            data: { selectedOptions: countryAsArray },
            field: 'selectedOptions',
            onValidate: onValidateCountry,
          }}
        >
          {countryKeyValues.map(({ code, name }) => (
            <Option
              key={code}
              value={code}
              className={`country-selector-option-${name}`}
            >
              {i18n.getString(name)}
            </Option>
          ))}
        </RequiredDropdown>
      </Field>
    </Card>
  );
};

CountryCard.propTypes = {
  country: PropTypes.string,
  onChangeCountry: PropTypes.func.isRequired,
  showError: PropTypes.bool,
};

CountryCard.defaultProps = {
  country: null,
  showError: false,
};
